import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Add, Info, Lock, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Pagination } from '@mui/material';
import { tagsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { TagDetailDto } from '../types';

const Tags: React.FC = () => {
  const { isAdmin } = useAuth();
  const [tags, setTags] = useState<TagDetailDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openStatsDialog, setOpenStatsDialog] = useState(false);
  const [tagStats, setTagStats] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', color: '' });
  const [editingTag, setEditingTag] = useState<TagDetailDto | null>(null);
  const [filterName, setFilterName] = useState('');
  const [sortColumn, setSortColumn] = useState<'id' | 'name' | 'usageCount' | 'createdByUserName' | 'createdAt' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // Advanced filter state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterById, setFilterById] = useState('');
  const [filterMinUsage, setFilterMinUsage] = useState('');
  const [filterMaxUsage, setFilterMaxUsage] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);

      // Convert filter values to appropriate types
      const params: any = {
        page: currentPage,
        pageSize
      };

      if (filterName) params.filterName = filterName;
      if (filterById) params.filterId = parseInt(filterById);
      if (filterMinUsage) params.filterMinUsage = filterMinUsage;
      if (filterMaxUsage) params.filterMaxUsage = filterMaxUsage;
      if (filterCreatedBy) params.filterCreatedBy = filterCreatedBy;
      if (filterDateFrom) params.filterDateFrom = filterDateFrom;
      if (filterDateTo) params.filterDateTo = filterDateTo;

      const response = await tagsApi.getAllTags(params);
      setTags(response.data);
      setTotalPages(response.totalPages);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterName, filterById, filterMinUsage, filterMaxUsage, filterCreatedBy, filterDateFrom, filterDateTo]);

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterName, filterById, filterMinUsage, filterMaxUsage, filterCreatedBy, filterDateFrom, filterDateTo]);

  // Fetch tags when page or filters change
  useEffect(() => {
    const loadTags = async () => {
      await fetchTags();
      await fetchTagStatistics();
    };
    loadTags();
  }, [fetchTags]);

  // Refetch tags when window comes into focus (page is revisited)
  useEffect(() => {
    const handleFocus = async () => {
      await fetchTags();
      await fetchTagStatistics();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchTags]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const fetchTagStatistics = async () => {
    try {
      const stats = await tagsApi.getTagStatistics();
      setTagStats(stats);
    } catch (err) {
      console.error('Failed to fetch tag statistics:', err);
    }
  };

  const handleOpenCreateDialog = () => {
    setFormData({ name: '', color: generateRandomColor() });
    setEditingTag(null);
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (tag: TagDetailDto) => {
    setEditingTag(tag);
    setFormData({ name: tag.name, color: tag.color || '' });
    setOpenEditDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setFormData({ name: '', color: '' });
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setFormData({ name: '', color: '' });
    setEditingTag(null);
  };

  const handleCreateTag = async () => {
    if (!formData.name.trim()) {
      setError('Tag name is required');
      showToast('Tag name is required', 'error');
      return;
    }

    try {
      await tagsApi.createTag({
        name: formData.name.trim(),
        color: formData.color,
      });
      setSuccess('Tag created successfully!');
      showToast('Tag created successfully!', 'success');
      handleCloseCreateDialog();
      setCurrentPage(1);
      // Refetch tags and statistics to update the UI immediately after creation
      await fetchTags();
      await fetchTagStatistics();
    } catch (err: any) {
      console.error('Failed to create tag:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create tag';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !formData.name.trim()) {
      setError('Tag name is required');
      showToast('Tag name is required', 'error');
      return;
    }

    try {
      await tagsApi.updateTag(editingTag.id, {
        name: formData.name.trim(),
        color: formData.color,
      });
      setSuccess('Tag updated successfully!');
      showToast('Tag updated successfully!', 'success');
      handleCloseEditDialog();
      // Refetch tags and statistics to update the UI immediately after update
      await fetchTags();
      await fetchTagStatistics();
    } catch (err: any) {
      console.error('Failed to update tag:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update tag';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const handleDeleteTag = async (tagId: number, tagName: string, usageCount: number) => {
    // Check if tag is in use
    if (usageCount > 0) {
      const errorMessage = `Cannot delete tag "${tagName}" because it is used by ${usageCount} source${usageCount !== 1 ? 's' : ''}. Please remove the tag from all sources before deleting it.`;
      setError(errorMessage);
      showToast(errorMessage, 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the tag "${tagName}"?`)) {
      return;
    }

    try {
      await tagsApi.deleteTag(tagId);
      setSuccess('Tag deleted successfully!');
      showToast('Tag deleted successfully!', 'success');
      setCurrentPage(1);
      // Refetch tags to update the UI immediately after deletion
      await fetchTags();
      await fetchTagStatistics();
    } catch (err: any) {
      console.error('Failed to delete tag:', err);
      const errorMessage = err.response?.data?.message || 'Failed to delete tag';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, color: e.target.value });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, name: e.target.value });
  };

  const generateRandomColor = (): string => {
    const colors = ['#1976D2', '#388E3C', '#D32F2F', '#F57C00', '#7B1FA2', '#0097A7', '#C2185B', '#E64A19', '#512DA8', '#00796B'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCreatorName = (createdByUserName: string): string => {
    // Show the username, default to "Unknown" if not available
    return createdByUserName || 'Unknown';
  };

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  };

  const handleClearFilters = () => {
    setFilterName('');
    setFilterById('');
    setFilterMinUsage('');
    setFilterMaxUsage('');
    setFilterCreatedBy('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setCurrentPage(1);
  };

  const hasActiveFilters = (): boolean => {
    return !!(filterName || filterById || filterMinUsage || filterMaxUsage || filterCreatedBy || filterDateFrom || filterDateTo);
  };

  // Get unique tag IDs from current tags
  const getTagIds = (): number[] => {
    const ids = Array.from(new Set(tags.map(t => t.id)));
    return ids.sort((a, b) => a - b);
  };

  // Get unique creator names from current tags
  const getCreatorNames = (): string[] => {
    const names = Array.from(new Set(tags.map(t => t.createdByUserName).filter(name => name && name !== 'Unknown')));
    return names.sort();
  };

  // Get usage count ranges
  const getUsageCountRanges = (): Array<{ label: string; min: number; max?: number }> => {
    return [
      { label: '0-5', min: 0, max: 5 },
      { label: '6-10', min: 6, max: 10 },
      { label: '11-20', min: 11, max: 20 },
      { label: '21-50', min: 21, max: 50 },
      { label: '50+', min: 50 },
    ];
  };

  const handleSortClick = (column: 'id' | 'name' | 'usageCount' | 'createdByUserName' | 'createdAt') => {
    if (sortColumn === column) {
      // If clicking the same column, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it with ascending order
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort tags based on selected column (filtering is done on backend)
  const getSortedTags = () => {
    let sorted = [...tags];

    if (sortColumn) {
      sorted.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (sortColumn) {
          case 'id':
            aVal = a.id;
            bVal = b.id;
            break;
          case 'name':
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case 'usageCount':
            aVal = a.usageCount;
            bVal = b.usageCount;
            break;
          case 'createdByUserName':
            aVal = a.createdByUserName.toLowerCase();
            bVal = b.createdByUserName.toLowerCase();
            break;
          case 'createdAt':
            aVal = new Date(a.createdAt).getTime();
            bVal = new Date(b.createdAt).getTime();
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return sorted;
  };

  const filteredTags = getSortedTags();

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Tag Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create, edit, and manage tags for your sources
          </Typography>
        </Box>
        {/* Pagination in top right */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Typography variant="caption" color="textSecondary">
            Page {currentPage} of {totalPages}
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
          />
        </Box>
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access to tags. Only admins can create, edit, or delete tags.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics Card */}
      {tagStats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 2fr' }, gap: 2, mb: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Total Tags
              </Typography>
              <Typography variant="h5">{tagStats.totalTags}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Most Used
              </Typography>
              <Typography variant="h5">
                {tagStats.mostUsedTags && tagStats.mostUsedTags.length > 0
                  ? tagStats.mostUsedTags[0].usageCount
                  : 0}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Most Used Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {tagStats.mostUsedTags && tagStats.mostUsedTags.length > 0 ? (
                      tagStats.mostUsedTags.slice(0, 3).map((tag: any) => (
                        <Chip
                          key={tag.id}
                          label={`${tag.name} (${tag.usageCount})`}
                          size="small"
                          style={{
                            backgroundColor: tag.color || '#ccc',
                            color: '#fff',
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        No tags yet
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Actions Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Filter tags..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              sx={{ flex: 1, minWidth: '200px' }}
            />
            {isAdmin ? (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenCreateDialog}
              >
                New Tag
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled
                startIcon={<Lock />}
                title="Only admins can create tags"
              >
                New Tag
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Info />}
              onClick={() => setOpenStatsDialog(true)}
            >
              View Stats
            </Button>
            <Button
              variant="outlined"
              startIcon={showAdvancedFilters ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              sx={{ ml: 'auto' }}
            >
              {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
              {hasActiveFilters() && <Chip label={`${Object.values({ filterName, filterById, filterMinUsage, filterMaxUsage, filterCreatedBy, filterDateFrom, filterDateTo }).filter(v => v).length}`} size="small" sx={{ ml: 1 }} />}
            </Button>
          </Box>

          {/* Advanced Filters Panel */}
          <Collapse in={showAdvancedFilters} timeout="auto">
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                {/* Tag ID Dropdown */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Tag ID</InputLabel>
                  <Select
                    value={filterById}
                    onChange={(e) => setFilterById(e.target.value)}
                    label="Tag ID"
                  >
                    <MenuItem value="">All</MenuItem>
                    {getTagIds().map(id => (
                      <MenuItem key={id} value={id.toString()}>
                        {id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Min Usage Count Dropdown */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Min Usage Count</InputLabel>
                  <Select
                    value={filterMinUsage}
                    onChange={(e) => setFilterMinUsage(e.target.value)}
                    label="Min Usage Count"
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUsageCountRanges().map(range => (
                      <MenuItem key={range.label} value={range.label}>
                        {range.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Max Usage Count Dropdown */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Max Usage Count</InputLabel>
                  <Select
                    value={filterMaxUsage}
                    onChange={(e) => setFilterMaxUsage(e.target.value)}
                    label="Max Usage Count"
                  >
                    <MenuItem value="">All</MenuItem>
                    {getUsageCountRanges().map(range => (
                      <MenuItem key={range.label} value={range.label}>
                        {range.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Created By Dropdown */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Created By</InputLabel>
                  <Select
                    value={filterCreatedBy}
                    onChange={(e) => setFilterCreatedBy(e.target.value)}
                    label="Created By"
                  >
                    <MenuItem value="">All</MenuItem>
                    {getCreatorNames().map(name => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Created From Date */}
                <TextField
                  size="small"
                  label="Created From"
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                {/* Created To Date */}
                <TextField
                  size="small"
                  label="Created To"
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="text"
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters()}
                  size="small"
                >
                  Clear All Filters
                </Button>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Tags Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell
                align="center"
                onClick={() => handleSortClick('id')}
                sx={{
                  width: '60px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#eeeeee' },
                }}
              >
                ID {sortColumn === 'id' && (
                  <span style={{ fontSize: '0.8em', marginLeft: '4px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </TableCell>
              <TableCell
                onClick={() => handleSortClick('name')}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#eeeeee' },
                }}
              >
                Tag Name {sortColumn === 'name' && (
                  <span style={{ fontSize: '0.8em', marginLeft: '4px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </TableCell>
              <TableCell align="center">Color</TableCell>
              <TableCell
                align="center"
                onClick={() => handleSortClick('usageCount')}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#eeeeee' },
                }}
              >
                Usage Count {sortColumn === 'usageCount' && (
                  <span style={{ fontSize: '0.8em', marginLeft: '4px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </TableCell>
              <TableCell
                onClick={() => handleSortClick('createdByUserName')}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#eeeeee' },
                }}
              >
                Created By {sortColumn === 'createdByUserName' && (
                  <span style={{ fontSize: '0.8em', marginLeft: '4px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </TableCell>
              <TableCell
                onClick={() => handleSortClick('createdAt')}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: '#eeeeee' },
                }}
              >
                Created Date {sortColumn === 'createdAt' && (
                  <span style={{ fontSize: '0.8em', marginLeft: '4px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography>Loading tags...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredTags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    {tags.length === 0 ? 'No tags yet. Create one to get started!' : 'No tags match your filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTags.map((tag) => (
                <TableRow key={tag.id} hover>
                  <TableCell align="center" sx={{ fontSize: '0.85em', color: '#666' }}>
                    {tag.id}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{tag.name}</TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: tag.color || '#ccc',
                        borderRadius: '4px',
                        margin: '0 auto',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={tag.usageCount} size="small" />
                  </TableCell>
                  <TableCell title={tag.createdByUserId}>
                    {formatCreatorName(tag.createdByUserName)}
                  </TableCell>
                  <TableCell>{formatDate(tag.createdAt)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(tag)}
                      color="primary"
                      title={isAdmin ? "Edit tag" : "Only admins can edit tags"}
                      disabled={!isAdmin}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTag(tag.id, tag.name, tag.usageCount)}
                      color="error"
                      title={isAdmin ? "Delete tag" : "Only admins can delete tags"}
                      disabled={!isAdmin}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Tag Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tag</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Tag Name"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="e.g., JavaScript, Tutorial, Important"
            sx={{ mb: 2 }}
            autoFocus
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input
              type="color"
              value={formData.color}
              onChange={handleColorChange}
              style={{ width: 60, height: 40, cursor: 'pointer', border: 'none', borderRadius: '4px' }}
            />
            <Box
              sx={{
                width: 60,
                height: 40,
                backgroundColor: formData.color,
                borderRadius: '4px',
                border: '1px solid #ddd',
              }}
            />
            <Button size="small" onClick={() => setFormData({ ...formData, color: generateRandomColor() })}>
              Random
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button onClick={handleCreateTag} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tag Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tag: {editingTag?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Tag Name"
            value={formData.name}
            onChange={handleNameChange}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <input
              type="color"
              value={formData.color}
              onChange={handleColorChange}
              style={{ width: 60, height: 40, cursor: 'pointer', border: 'none', borderRadius: '4px' }}
            />
            <Box
              sx={{
                width: 60,
                height: 40,
                backgroundColor: formData.color,
                borderRadius: '4px',
                border: '1px solid #ddd',
              }}
            />
            <Button size="small" onClick={() => setFormData({ ...formData, color: generateRandomColor() })}>
              Random
            </Button>
          </Box>
          {editingTag && (
            <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <Typography variant="caption">
                <strong>Created by:</strong> {editingTag.createdByUserName}
              </Typography>
              <br />
              <Typography variant="caption">
                <strong>Created:</strong> {formatDate(editingTag.createdAt)}
              </Typography>
              <br />
              <Typography variant="caption">
                <strong>Usage:</strong> {editingTag.usageCount} source{editingTag.usageCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateTag} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Statistics Dialog */}
      <Dialog open={openStatsDialog} onClose={() => setOpenStatsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Tag Statistics</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {tagStats ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Total Tags: {tagStats.totalTags}
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom>
                Most Used Tags:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tagStats.mostUsedTags && tagStats.mostUsedTags.length > 0 ? (
                  tagStats.mostUsedTags.map((tag: any, index: number) => (
                    <Box key={tag.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ minWidth: '30px' }}>{index + 1}.</Typography>
                      <Chip
                        label={tag.name}
                        style={{
                          backgroundColor: tag.color || '#ccc',
                          color: '#fff',
                        }}
                      />
                      <Typography>{tag.usageCount} sources</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="textSecondary">No tags found</Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Typography>Loading statistics...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Tags;
// End of pages/Tags.tsx
